import React from "react";

/**
 * Campo numérico editable.
 *
 * EL PROBLEMA QUE RESUELVE
 * ------------------------
 * El patrón habitual —`value={numero}` con `onChange={e => set(Number(e.target.value))}`—
 * tiene dos fallas que en un formulario de peso y balance son graves:
 *
 *   1. `Number("")` es 0. Al vaciar el campo el estado pasa a 0, React vuelve a
 *      escribir "0" en el input, y a partir de ahí cada tecla se agrega DETRÁS
 *      del cero. El piloto que quiere reemplazar 540 por 400 termina con "0400".
 *
 *   2. Si el texto tecleado parsea al mismo número que ya había —"0540" contra
 *      540— el estado no cambia, React no vuelve a renderizar, y el input se
 *      queda mostrando el texto raro. Es el "095" que aparecía en el campo de
 *      piloto.
 *
 * Ambas cosas se ven como "el campo no responde", que es justo lo que reportó
 * el operador.
 *
 * CÓMO LO RESUELVE
 * ----------------
 * El texto que el usuario escribe vive acá, no en el estado del padre. Al
 * padre solo se le manda el número cuando el texto representa uno. El campo
 * puede quedar vacío, o a medio escribir ("-", "1."), sin que nadie lo
 * corrija por debajo mientras se teclea.
 *
 * Cuando el valor cambia desde afuera —otro control, una conversión de
 * unidades, un cálculo— el texto se sincroniza, pero solo si el número es
 * distinto del que ya se está mostrando: así escribir "0540" no provoca que el
 * campo salte a "540" en mitad de la edición.
 *
 * Al perder el foco, el texto se normaliza al número. Si quedó vacío, se
 * informa el valor de respaldo (0 por defecto) para no dejar el cálculo con un
 * hueco silencioso.
 */
export default function CampoNumero({
  valor,
  alCambiar,
  respaldoAlVaciar = 0,
  permitirVacio = false,
  style,
  ...resto
}) {
  const [texto, setTexto] = React.useState(() => (valor === null || valor === undefined ? "" : String(valor)));
  const [editando, setEditando] = React.useState(false);

  // Sincronizar desde afuera, sin pisar lo que se está tecleando.
  React.useEffect(() => {
    if (editando) return;
    const nuevo = valor === null || valor === undefined ? "" : String(valor);
    if (Number(nuevo) !== Number(texto) || nuevo === "" || texto === "") setTexto(nuevo);
    // `texto` a propósito fuera de las dependencias: solo interesa reaccionar a
    // cambios del valor externo, no a los que produce el propio tecleo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valor, editando]);

  const manejar = (e) => {
    const t = e.target.value;
    setTexto(t);
    if (t === "" || t === "-" || t === "." || t === "-.") {
      // Estado intermedio: no se informa nada todavía. Informar 0 acá es lo que
      // hacía imposible vaciar el campo.
      if (permitirVacio) alCambiar(null);
      return;
    }
    const n = Number(t);
    if (Number.isFinite(n)) alCambiar(n);
  };

  const alSalir = () => {
    setEditando(false);
    if (texto === "" || !Number.isFinite(Number(texto))) {
      if (permitirVacio) { setTexto(""); alCambiar(null); return; }
      setTexto(String(respaldoAlVaciar));
      alCambiar(respaldoAlVaciar);
      return;
    }
    setTexto(String(Number(texto)));
    alCambiar(Number(texto));
  };

  return (
    <input
      {...resto}
      type="number"
      style={style}
      value={texto}
      onFocus={() => setEditando(true)}
      onChange={manejar}
      onBlur={alSalir}
    />
  );
}
